import { useEffect, useState } from 'react';
import Widget from '../../../components/Dashboard/Widget';
import { useDashboardControls } from 'contexts/Dashboard/useDashboardControls';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { KPIsWidgetEnum, LongTitles } from '../utils';
import { oceanBlue, orange } from 'constants/Colors';
import { formatDate } from 'utils/date-helpers';
import { useKPIGetOBDNotUsed } from 'api/kpi/kpi';
import { GroupingLevel, KPIOBDDataPoint } from 'api/model';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload, faSpinner } from '@fortawesome/free-solid-svg-icons';
import Button from 'components/inputs/Button';
import { useExportExportOBDNotUsedHook } from 'api/export/export';
import { showException } from 'utils/exception-helper';
import { downloadBlob } from 'utils/file-utils';

const formatPeriodLabel = (date: Date, groupingLevel: GroupingLevel): string => {
  const d = new Date(date);
  switch (groupingLevel) {
    case GroupingLevel.Day:
      return formatDate(d);
    case GroupingLevel.Week: {
      // Get week number
      const startOfYear = new Date(d.getFullYear(), 0, 1);
      const days = Math.floor((d.getTime() - startOfYear.getTime()) / (24 * 60 * 60 * 1000));
      const weekNum = Math.ceil((days + startOfYear.getDay() + 1) / 7);
      return `v.${weekNum} ${d.getFullYear()}`;
    }
    case GroupingLevel.Month:
      return d.toLocaleDateString('sv-SE', { year: 'numeric', month: 'short' });
    case GroupingLevel.Year:
      return d.getFullYear().toString();
    default:
      return formatDate(d);
  }
};

const OBDNotUsedWidget = () => {
  const [chartData, setChartData] = useState<KPIOBDDataPoint[]>([]);
  const [isDownloading, setIsDownloading] = useState(false);
  const {
    dashboardState: {
      daterange,
      selectedCaseTypes,
      selectedAssignmentTypes,
      selectedSupplierIDs,
      selectedCustomerNames,
      selectedFromCountyIDs,
      selectedToCountyIDs,
      groupingLevel,
    },
  } = useDashboardControls();

  const getKPIOBDData = useKPIGetOBDNotUsed();

  const startDate = daterange[0];
  const endDate = daterange[1];

  useEffect(() => {
    if (startDate && endDate) {
      getKPIOBDData.mutate({
        data: {
          startDate,
          endDate,
          caseTypes: selectedCaseTypes,
          assignmentTypes: selectedAssignmentTypes,
          supplierIDs: selectedSupplierIDs,
          customerNames: selectedCustomerNames,
          fromCountyIDs: selectedFromCountyIDs,
          toCountyIDs: selectedToCountyIDs,
          groupBy: groupingLevel,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate?.getTime(), endDate?.getTime(), selectedCaseTypes, selectedAssignmentTypes, selectedSupplierIDs, selectedCustomerNames, selectedFromCountyIDs, selectedToCountyIDs, groupingLevel]);

  useEffect(() => {
    if (getKPIOBDData.data) {
      setChartData(
        getKPIOBDData.data.dataPoints
          .map((dp) => ({
            ...dp,
            date: new Date(dp.date),
          }))
          .sort((a, b) => a.date.getTime() - b.date.getTime())
      );
    }
  }, [getKPIOBDData.data]);

  const downloadExcel = useExportExportOBDNotUsedHook();

  return (
    <Widget
      title={LongTitles[KPIsWidgetEnum.OBDNotUsed]}
      count={undefined}
      isLoading={getKPIOBDData.isLoading}
      info="Visar uppdrag där OBD-mätare inte har använts eller inte verifierats.
        Procent visar andelen av totala uppdrag inom vald period och filter."
    >
      <p style={{ fontWeight: 'bold', textAlign: 'center' }}>
        OBS: Datumen utgår från ärendets stängd-datum.
      </p>

      <Button
        disabled={isDownloading || chartData.length === 0}
        onClick={async () => {
          try {
            setIsDownloading(true);
            const result = await downloadExcel({
              startDate: daterange[0]!,
              endDate: daterange[1]!,
              caseTypes: selectedCaseTypes,
              assignmentTypes: selectedAssignmentTypes,
              supplierIDs: selectedSupplierIDs,
              customerNames: selectedCustomerNames,
              fromCountyIDs: selectedFromCountyIDs,
              toCountyIDs: selectedToCountyIDs,
              groupBy: groupingLevel,
            });
            if (result) {
              downloadBlob(
                result,
                `Nyckeltal_EjAnvantOBD_${formatDate(daterange[0]!)}_${formatDate(daterange[1]!)}.xlsx`
              );
            }
          } catch (error) {
            showException(error);
          } finally {
            setIsDownloading(false);
          }
        }}
      >
        <FontAwesomeIcon icon={faDownload} /> Ladda ner
        {isDownloading ? (
          <FontAwesomeIcon icon={faSpinner} spin />
        ) : null}
      </Button>

      <ResponsiveContainer
        width="100%"
        height={300}
        initialDimension={{ width: 520, height: 400 }}
      >
        <ComposedChart
          height={400}
          data={chartData}
          margin={{ top: 5, right: 40, left: 5, bottom: 90 }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="date"
            tickFormatter={(date) => formatPeriodLabel(new Date(date), groupingLevel)}
            tickSize={chartData.length >= 7 ? 50 : 10}
            padding={{ left: 10, right: 10 }}
            angle={chartData.length >= 7 ? -80 : 0}
          />
          <YAxis yAxisId="left" orientation="left" stroke={orange} />
          <YAxis
            yAxisId="right"
            orientation="right"
            stroke={oceanBlue}
            domain={[0, 100]}
            tickFormatter={(value) => `${value}%`}
          />
          <Tooltip
            labelFormatter={(label) => formatPeriodLabel(new Date(label), groupingLevel)}
            formatter={(value: number, name: string) => {
              if (name === 'Andel utan OBD') {
                return [`${value.toFixed(1)}%`, name];
              }
              return [value, name];
            }}
          />
          <Legend verticalAlign="top" height={36} />
          <Bar
            yAxisId="left"
            dataKey="obdNotUsedCount"
            name="Antal utan OBD"
            fill={orange}
            opacity={0.8}
          />
          <Bar
            yAxisId="left"
            dataKey="totalAssignments"
            name="Totalt antal uppdrag"
            fill="#cccccc"
            opacity={0.5}
          />
          <Line
            yAxisId="right"
            type="monotone"
            dataKey="obdNotUsedPercentage"
            name="Andel utan OBD"
            stroke={oceanBlue}
            strokeWidth={2}
            dot={{ fill: oceanBlue, r: 4 }}
          />
        </ComposedChart>
      </ResponsiveContainer>
      {chartData.length === 0 && (
        <div style={{ textAlign: 'center', marginTop: '20px' }}>
          {getKPIOBDData.isLoading ? (
            <FontAwesomeIcon icon={faSpinner} spin />
          ) : (
            <p>Ingen data att visa för vald filtrering.</p>
          )}
        </div>
      )}
    </Widget>
  );
};

export default OBDNotUsedWidget;
